import { colors, colors2, CheckboxButton } from "oolib";
import { ActionMenu } from "../../../../UI/ActionMenu";
import React, { useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate, useLocation } from "react-router-dom";
import * as ExpandComps from "../../CONFIG_PARSER/ExpandComps";
import {
  __CheckAccessToContentType,
  __IsAuthor,
} from "../../../../../utils/accessControl";
import { stitchLink } from "../../../../../utils/general";
import { TableCell, TableRow } from "./comps";
import { StyledExpandCompContainer, StyledExpandCompWrapper } from "./styled";
import { __GetContentTypeConfigNew } from "../../../../../utils/getters/gettersV2";
import { ROW_HEIGHT } from "../..";
import styled, { css } from "styled-components";
import { StyledCheckboxContainer, StyledRowCellContentWrapper } from "../../styled";
import { parseSpecialConfigSyntax } from "@okf/ootils";
import { TABLE_BORDER_RADIUS } from "../../CONSTANTS";

const StyledTableRowWrapper = styled(TableRow)`
  /* The row's paint as a variable, so the pinned first cell (styled.js,
     applyFreezeFirstColCss) can repeat it over an opaque base — the greys
     are translucent, and that cell must hide the columns it slides over. */
  --row-bg: ${({ disableRowStripes, rowIdx }) =>
    !disableRowStripes && rowIdx % 2 !== 0 ? colors2.grey3 : colors2.white};
  background-color: var(--row-bg);
  border-top: ${({ rowIdx, disableRowStripes }) =>
    !disableRowStripes && rowIdx !== 0
      ? `1px solid ${colors2.grey10}`
      : "none"};

  ${({ rowIsClickable }) =>
    rowIsClickable &&
    css`
      &:hover {
        --row-bg: ${colors2.grey5};

        ${StyledRowCellContentWrapper}::after {
          background: linear-gradient(
            to left,
            color-mix(in srgb, ${colors2.grey5}, ${colors2.white}),
            hsla(0, 0%, 100%, 0)
          );
        }
      }
    `}
`;

const TableRowElem = ({
  id: rowId,
  data,
  tableConfig,
  actions,
  bulkActions,
  actionsConfig: actionsConfigProp,
  conditionsConfig: conditionsConfigProp,
  colWidthConfig,
  handleRowClick: parentHandleRowClick,
  handleCellChange,
  rowIdx,
  isRowSelected,
  onCheckboxDragStart,
  onCheckboxDragOver,
  requestRowDelete,
  isLastRow,
  lastCellIsTableEdge,
  disableRowStripes,
}) => {
  let queryClient = useQueryClient();
  let { data: userData } = queryClient.getQueryState("userData");

  const tableElemRow_ref = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const { link: linkConfig, expandComp: expandCompConfig } = tableConfig;
  const link = linkConfig 
  ? typeof linkConfig === "string"
    ? parseSpecialConfigSyntax({config: linkConfig, content: data, returnUndefinedIfReplacementWordNotFound: true}) //this is a newer method. a shorthand so more convenient. here link is defined as a string. read more in the function
    : stitchLink(data, linkConfig)  // here link is defined as a config object that stitchLink parses. slightly cumbersome syntax
  : undefined;

  const ExpandComp = expandCompConfig && ExpandComps[expandCompConfig.comp];

  const ExpandCompWrapperRef = useRef(null);

  const [showExpandComp, setShowExpandComp] = useState(false);

  const handleRowClick = (e, data) => {
    if (expandCompConfig) {
      setShowExpandComp((prev) => !prev);
    } else if (parentHandleRowClick) {
      parentHandleRowClick(data);
    }

    e.stopPropagation();
  };

  const conditionsConfig = {
    //functions that decide weather an action should be shown to current user
    allowEdit: (data) => {
      let contentTypeConfig = __GetContentTypeConfigNew(
        data.meta.kp_content_type
      );
      switch (contentTypeConfig.general.segment) {
        case "publishing":
        default:
          return data.meta.kp_contributed_by?._id === userData?.user._id;
        case "staticPages":
        case "collections":
          return (
            __CheckAccessToContentType(
              userData?.user,
              "UPDATE",
              data.meta.kp_content_type
            ) &&
            (data.kp_published_status === "published" ||
              (data.currentEditor?._id || data.currentEditor) ===
                userData?.user._id)
          );
      }
    },
    allowModerate: (data) => {
      return !__IsAuthor(
        data.meta.kp_contributed_by?._id,
        userData?.user?._id
      ) &&
        __CheckAccessToContentType(
          userData.user,
          "MODERATE",
          data.meta.kp_content_type
        )
        ? true
        : false;
    },
    allowDelete: (data) => {
      let contentTypeConfig = __GetContentTypeConfigNew(
        data.meta.kp_content_type
      );

      // if content is under edit then don't show the delete option, because the api doesn't delete the clone doc used for editing "

      if (data.currentEditor) return false;

      switch (contentTypeConfig.general.segment) {
        case "publishing":
        default:
          return (
            __IsAuthor(data.meta.kp_contributed_by?._id, userData?.user._id) ||
            __CheckAccessToContentType(
              userData.user,
              "MODERATE",
              data.meta.kp_content_type
            )
          );
        case "staticPages":
        case "collections":
          return (
            __CheckAccessToContentType(
              userData?.user,
              "DELETE",
              data.meta.kp_content_type
            ) &&
            (data.kp_published_status === "published" ||
              (data.currentEditor?._id || data.currentEditor) ===
                userData?.user._id)
          );
      }
    },
    ...(conditionsConfigProp ? conditionsConfigProp : {}),
  };

  const actionsConfig = {
    fireUpdateAction: () => {
      if (link) {
        /*
        '&' because data.link will be 'www.foo.bar?id=123'
        */
        let path = `${link}&createUpdate=create`;
        navigate(path, {
          state: { postPblRedirect: `${location.pathname}${location.search}` },
        });
      }
      //redirect to the case report page
    },
    editContent: () => {
      //redirect to the case report page
      navigate(
        `/edit/${data.meta.kp_content_type}?id=${
          data.kp_og_published_doc || data._id
        }`
      ); // ----> edit
    },
    viewPublishedContent: () => {
      //redirect to the case report page
      const docId =
        data.kp_published_status === "editPublished"
          ? data.kp_og_published_doc
          : data._id;
      navigate(`/published-page/${data.meta.kp_content_type}?id=${docId}`); // ----> edit
    },
    fireEditReport: () => {
      // deprecate after replaced in nazdeek table config
      //redirect to the case report page
      navigate(`/edit/${data.meta.kp_content_type}?id=${rowId}`); // ----> edit
    },
    fireModerateReport: () => {
      //redirect to the case report page
      navigate(`/moderate/${data.meta.kp_content_type}?id=${rowId}`); // ----> moderate
    },
    deleteContent: () => {
      // delete confirm modal + mutation live at TableElem level (single
      // instance for the whole table); we just flag this row for deletion
      requestRowDelete(data);
    },
    ...(actionsConfigProp ? actionsConfigProp : {}),
  };

  const rowIsClickable = expandCompConfig || link || parentHandleRowClick;

  // Rows the host has taken out of bulk selection (tableConfig.disabledRowIds
  // — e.g. documents an in-flight annotation run still has pending). The row
  // stays readable and clickable; only its checkbox is off, and the label
  // says why on hover.
  const rowIsDisabled =
    Array.isArray(tableConfig.disabledRowIds) && tableConfig.disabledRowIds.includes(rowId);

  return (
    <div>
      <StyledTableRowWrapper
        rowIsClickable={rowIsClickable}
        rowIdx={rowIdx}
        disableRowStripes={disableRowStripes}
        link={link}
        parentRef={tableElemRow_ref}
        title={rowIsDisabled ? tableConfig.disabledRowLabel || undefined : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          ...(rowIsClickable ? { cursor: "pointer" } : {}),
          ...(rowIsDisabled ? { opacity: 0.55 } : {}),
          position: "relative",
          height: (tableConfig.rowHeight || ROW_HEIGHT) + "px",
          ...(isLastRow
            ? { 
              borderBottomLeftRadius: TABLE_BORDER_RADIUS+'px', 
              borderBottomRightRadius: TABLE_BORDER_RADIUS+'px'
            } : {})
        }}
        onClick={(e) => handleRowClick(e, data)}
      >
        {bulkActions && (
          // Toggling happens on mousedown via the table's drag select, so no
          // onChange on the CheckboxButton (its click would double-toggle).
          <StyledCheckboxContainer
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              if (e.button !== 0 || rowIsDisabled) return;
              e.preventDefault(); // no text selection while dragging
              onCheckboxDragStart(rowIdx, isRowSelected);
            }}
            onMouseEnter={() => onCheckboxDragOver(rowIdx)}
          >
            <CheckboxButton value={isRowSelected} disabled={rowIsDisabled} />
          </StyledCheckboxContainer>
        )}
        {tableConfig.cellData.map((d, i) => (
          <TableCell
            tableConfig={tableConfig}
            rowIdx={rowIdx}
            disableRowStripes={disableRowStripes}
            cellIdx={i}
            isLastRow={isLastRow}
            isTableEdge={lastCellIsTableEdge && i === tableConfig.cellData.length - 1}
            key={d.colId}
            colWidthConfig={colWidthConfig}
            rowId={rowId}
            data={data}
            colConfig={d}
            handleCellChange={handleCellChange}
            disabled={d.disabled}
          />
        ))}
        {actions && (
          <div style={{ padding: "0.6rem" }}>
          <ActionMenu
            style={{ maxWidth: 160 }}
            actions={actions
              .map((d) =>
                d.condition
                  ? conditionsConfig[d.condition](data)
                    ? {
                        display: d.display,
                        onClick: (e) => {
                          e.stopPropagation();
                          actionsConfig[d.action](e, data);
                        },
                        confirmAction: d.confirmAction,
                      }
                    : undefined
                  : {
                      display: d.display,
                      onClick: (e) => {
                        e.stopPropagation();
                        actionsConfig[d.action](e, data);
                      },
                      confirmAction: d.confirmAction,
                    }
              )
              .filter(Boolean)}
          />
          </div>
        )}
      </StyledTableRowWrapper>

      {expandCompConfig && ( //we need tat showExpandedComp stuff for the out-transition cuz it has a delay
        <StyledExpandCompContainer
          id="ExpandCompContainer"
          {...{
            showExpandComp,
            contentsHeight:
              ExpandCompWrapperRef.current?.getBoundingClientRect().height,
          }}
        >
          <StyledExpandCompWrapper>
            <div
              style={{ padding: "1rem", backgroundColor: colors.greyColor10 }}
              ref={ExpandCompWrapperRef}
            >
              <ExpandComp
                rowId={rowId}
                parentRowRawData={data}
                {...expandCompConfig.props}
              />
            </div>
          </StyledExpandCompWrapper>
        </StyledExpandCompContainer>
      )}
    </div>
  );
};

export default React.memo(TableRowElem);
